/**
 * The image wrapped back onto the hole it came from.
 *
 * An unrolled image is the form you interpret on and the form nobody can read
 * spatially: a fracture crossing the seam at bin 0 appears as two unrelated
 * half-sinusoids at opposite edges. Wrapping it back onto a cylinder costs half
 * the data — only the near side is visible — and buys the one thing the flat
 * view cannot show, which is what the feature actually looks like going round
 * the hole.
 *
 * Rendered pixel by pixel rather than as a CSS 3D transform, because the
 * horizontal compression towards the edges is not a perspective transform: at
 * screen offset u the visible azimuth is asin(u), which no affine or
 * perspective matrix produces.
 */

import { useEffect, useMemo, useRef } from "react";
import { useRampsVersion } from "../hooks";
import { rampColor, rampStops } from "../lib/colorscale";
import {
  type Feature,
  type ImageWindow,
  depthAt,
  pixelsFrom,
  stretchFraction,
  type ByteStretch,
} from "../lib/imagelog";

export function BoreholeCylinder({
  win,
  which,
  ramp,
  reverse,
  stretch,
  viewAzimuth,
  width,
  height,
  features,
  holeDiameter,
  onRotate,
}: {
  win: ImageWindow | null;
  which: "static" | "dynamic";
  ramp: string;
  reverse?: boolean;
  stretch?: ByteStretch;
  /** Compass bearing facing the viewer, degrees. */
  viewAzimuth: number;
  width: number;
  height: number;
  features?: Feature[];
  holeDiameter: number;
  onRotate?: (azimuth: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ x: number; from: number } | null>(null);
  const pixels = useMemo(() => (win ? pixelsFrom(win, which) : null), [win, which]);
  const versi = useRampsVersion();

  //: Tinggi setengah-sumbu elips tutup, sebagai bagian dari lebar silinder.
  const capRatio = 0.13;
  const cap = width * capRatio;
  const bodyTop = cap;
  const bodyHeight = Math.max(height - cap * 2, 10);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    if (!pixels) return;

    const stops = rampStops(ramp);
    const lut: string[] = [];
    for (let i = 0; i < 256; i++) {
      const f = stretchFraction(i, stretch);
      lut.push(rampColor(stops, reverse ? 1 - f : f));
    }
    const rgb = lut.map((css) => {
      const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(css);
      return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : [0, 0, 0];
    });

    const out = context.createImageData(width, height);
    for (let x = 0; x < width; x++) {
      const u = (x / (width - 1)) * 2 - 1;
      if (Math.abs(u) > 1) continue;
      const offset = (Math.asin(u) * 180) / Math.PI;
      const azimuth = ((viewAzimuth + offset) % 360 + 360) % 360;
      const column = Math.min(
        pixels.bins - 1,
        Math.max(0, Math.round((azimuth / 360) * pixels.bins)),
      );
      // Cahaya Lambert sederhana: paling terang di tengah, redup di tepi. Ia
      // ada bukan sebagai hiasan — tanpa gradasi itu silindernya terbaca
      // sebagai persegi panjang, dan seluruh gunanya hilang.
      const shade = 0.45 + 0.55 * Math.cos((offset * Math.PI) / 180);
      // Tepi tutupnya melengkung: baris teratas yang terlihat di tengah lebih
      // tinggi daripada di pinggir.
      const curve = cap * Math.cos((offset * Math.PI) / 180);
      for (let y = 0; y < height; y++) {
        const t = (y - (bodyTop + curve)) / bodyHeight;
        if (t < 0 || t > 1) continue;
        const row = Math.min(pixels.rows - 1, Math.max(0, Math.round(t * (pixels.rows - 1))));
        const index = row * pixels.bins + column;
        const at = (y * width + x) * 4;
        if (!pixels.mask[index]) {
          // Sela pad tetap sela, juga di sini: digambar kelabu redup, bukan
          // diwarnai seolah terukur.
          out.data[at] = out.data[at + 1] = out.data[at + 2] = Math.round(150 * shade);
          out.data[at + 3] = 190;
          continue;
        }
        const colour = rgb[pixels.data[index]];
        out.data[at] = Math.round(colour[0] * shade);
        out.data[at + 1] = Math.round(colour[1] * shade);
        out.data[at + 2] = Math.round(colour[2] * shade);
        out.data[at + 3] = 255;
      }
    }
    context.putImageData(out, 0, 0);

    // Tutup atas dan bawah, digambar sesudah badannya supaya tepinya bersih.
    context.strokeStyle = "rgba(20,20,20,0.75)";
    context.lineWidth = 1.4;
    for (const centre of [bodyTop, bodyTop + bodyHeight]) {
      context.beginPath();
      context.ellipse(width / 2, centre, width / 2 - 1, cap, 0, 0, Math.PI * 2);
      context.stroke();
    }
  }, [pixels, ramp, reverse, stretch?.min, stretch?.max, versi, viewAzimuth, width, height, cap,
      bodyTop, bodyHeight]);

  const top = win?.top ?? 0;
  const base = win?.base ?? 1;
  const span = base - top || 1;

  return (
    <div
      style={{ position: "relative", width, height, flex: "none", touchAction: "none" }}
      onPointerDown={(event) => {
        if (!onRotate) return;
        drag.current = { x: event.clientX, from: viewAzimuth };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (!drag.current || !onRotate) return;
        const moved = event.clientX - drag.current.x;
        onRotate(((drag.current.from - moved * 0.6) % 360 + 360) % 360);
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, cursor: onRotate ? "ew-resize" : "default" }}
      />
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        {features?.map((feature) => {
          // Hanya sisi yang menghadap yang digambar; separuh sinusoid yang di
          // belakang lubang tidak boleh terlihat menembusnya.
          const points: string[] = [];
          for (let offset = -88; offset <= 88; offset += 4) {
            const azimuth = ((viewAzimuth + offset) % 360 + 360) % 360;
            const md = depthAt(feature, azimuth, holeDiameter);
            const t = (md - top) / span;
            if (t < 0 || t > 1) continue;
            const x = ((Math.sin((offset * Math.PI) / 180) + 1) / 2) * (width - 1);
            const curve = cap * Math.cos((offset * Math.PI) / 180);
            points.push(`${x.toFixed(1)},${(bodyTop + curve + t * bodyHeight).toFixed(1)}`);
          }
          if (points.length < 2) return null;
          return (
            <polyline
              key={feature.id}
              points={points.join(" ")}
              fill="none"
              stroke={feature.color || "#35b8c6"}
              strokeWidth={1.6}
              opacity={0.95}
            />
          );
        })}
      </svg>
    </div>
  );
}
